// Detecció de codificació d'un fitxer de text (CSV) a partir d'una mostra de bytes.
// Pur i sense DOM (testejable): rep un ArrayBuffer/Uint8Array i torna
// { encoding, confidence, reason }.
//
//   confidence: 'high' → es pot autoseleccionar i continuar sense intervenció.
//               'low'  → ambigu/desconegut; la UI deixa la tria buida i obliga
//                        l'usuari a escollir abans de continuar.
//
// Estratègia (sense dependències, ràpida sobre una mostra):
//   1. BOM            → UTF-8 (alta) · UTF-16 → desconegut (baixa, no l'oferim).
//   2. UTF-8 vàlid    → UTF-8 (alta). ASCII pur també (idèntic en totes).
//   3. Byte únic      → Western (windows-1252, que cobreix també latin-1 al rang
//                        0xA0–0xFF). Si apareixen bytes 0x80–0x9F SENSE glif a
//                        cp1252 → és un altre codepage que no oferim → desconegut.

// Posicions 0x80–0x9F sense caràcter assignat a Windows-1252 (si hi són, gairebé
// segur que el fitxer és un altre codepage d'1 byte —cp1250/1251/…— que no oferim).
const CP1252_UNDEFINED = new Set([0x81, 0x8d, 0x8f, 0x90, 0x9d]);

const SUPPORTED_ENCODINGS = ['utf-8', 'latin1', 'windows-1252'];

function toBytes(input) {
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  if (ArrayBuffer.isView(input)) return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  return new Uint8Array(0);
}

// Recorre la mostra validant la gramàtica UTF-8. Com que la mostra pot tallar una
// seqüència multibyte al final, una seqüència incompleta JUST al final NO es
// considera error (truncatedTail), per no donar falsos negatius en fitxers grans.
function scanUtf8(bytes) {
  const n = bytes.length;
  let i = 0;
  let sawMultibyte = false;
  while (i < n) {
    const b = bytes[i];
    if (b < 0x80) { i += 1; continue; }
    let cont;
    if (b >= 0xc2 && b <= 0xdf) cont = 1;
    else if (b >= 0xe0 && b <= 0xef) cont = 2;
    else if (b >= 0xf0 && b <= 0xf4) cont = 3;
    else return { valid: false, sawMultibyte };
    if (i + cont >= n) {
      // No queden prou bytes per completar la seqüència. Si els que hi ha SÓN
      // continuacions vàlides, és un tall de la mostra (no és error); si no, és
      // un byte únic que casualment cau en rang de líder → error real.
      for (let k = 1; k < n - i; k += 1) {
        const c = bytes[i + k];
        if (c < 0x80 || c > 0xbf) return { valid: false, sawMultibyte };
      }
      return { valid: true, sawMultibyte: true, truncatedTail: true };
    }
    for (let k = 1; k <= cont; k += 1) {
      const c = bytes[i + k];
      if (c < 0x80 || c > 0xbf) return { valid: false, sawMultibyte };
    }
    sawMultibyte = true;
    i += cont + 1;
  }
  return { valid: true, sawMultibyte };
}

/**
 * Detecta la codificació d'una mostra de bytes d'un fitxer de text.
 *
 * @param {ArrayBuffer|Uint8Array} input Mostra de bytes (els primers KB del fitxer).
 * @return {{encoding: string|null, confidence: 'high'|'low', reason: string}}
 *         `encoding` és null quan no es pot determinar (confidence 'low').
 */
export function detectEncoding(input) {
  const bytes = toBytes(input);
  if (bytes.length === 0) return { encoding: 'utf-8', confidence: 'high', reason: 'empty' };

  // 1. BOM explícit.
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { encoding: 'utf-8', confidence: 'high', reason: 'bom-utf8' };
  }
  if (bytes.length >= 2 && ((bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[0] === 0xfe && bytes[1] === 0xff))) {
    return { encoding: null, confidence: 'low', reason: 'utf16' };
  }

  // 2. UTF-8 vàlid (inclou ASCII pur, idèntic en totes les codificacions).
  const u = scanUtf8(bytes);
  if (u.valid) return { encoding: 'utf-8', confidence: 'high', reason: u.sawMultibyte ? 'utf8' : 'ascii' };

  // 3. Codificació d'1 byte. Distingir Western (windows-1252) d'un codepage desconegut.
  let hasC1 = false;
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    if (b >= 0x80 && b <= 0x9f) {
      if (CP1252_UNDEFINED.has(b)) return { encoding: null, confidence: 'low', reason: 'unknown-codepage' };
      hasC1 = true;
    }
  }
  // Amb bytes 0x80–0x9F definits → segur cp1252; només 0xA0–0xFF → latin-1 ≡ cp1252.
  return { encoding: 'windows-1252', confidence: 'high', reason: hasC1 ? 'cp1252' : 'latin1' };
}

export { SUPPORTED_ENCODINGS };
